import { useApi } from "@/shared/api";
import { useToastNotifications } from "@/shared/hooks/useToastNotifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";
import type { PaymentStatusRequest, RequestPaymentRequest } from "./api.dto";
import { filterObjectKeys } from "@/shared/lib/utils";
import type {
  PaginatedResponse,
  PaginationRequest,
  TableApiCallParams,
} from "@/shared/types";
import { useMutate } from "@/shared/hooks";
import type { Payment } from "../model";

export const usePaymentStatus = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Статус оплаты изменен.",
      },
    },
    refetchQueries: ["payouts"],
  });
  const { api } = useApi();

  return useMutation<AxiosResponse, AxiosError, PaymentStatusRequest>({
    mutationFn: (data) =>
      api.patch(`/payments/${data.id}/status/${data.status}`),
    onSuccess,
    onError,
  });
};

export const usePayouts = (props: PaginationRequest) => {
  const { api } = useApi();
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<Payment>>({
    queryKey: ["payouts", params],
    queryFn: async () => {
      const response = await api.get("/payments", {
        params,
      });

      return response.data;
    },
  });
};

export const useApplications = ({
  pageSize,
  currentPage,
  search,
}: TableApiCallParams) => {
  const { api } = useApi();

  return useQuery<PaginatedResponse<Payment>>({
    queryKey: ["applications"],
    queryFn: async () => {
      const { data } = await api.get("/payments", {
        params: {
          pageSize,
          currentPage,
          search,
        },
      });
      return data;
    },
  });
};

export const useRequestPayment = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "post",
    url: "/payments/request",
    toast: {
      success: {
        description: "Вы запросили платёж.",
      },
      error: {
        description: "Недостаточно средств на балансе.",
      },
    },
    refetchQueries: ["balance"],
  });

  return useMutation<
    AxiosResponse,
    AxiosError,
    RequestPaymentRequest,
    Response
  >({
    mutationKey: ["updateUsername"],
    mutationFn,
    onSuccess,
    onError,
  });
};
